import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { createPortal } from 'react-dom';
import isEmpty from 'lodash/isEmpty';
import { ConfirmGoalCreationModal } from 'modules/predictiveGoals/components/modals/ConfirmGoalCreationModal';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { usePredictiveGoalsContext } from 'modules/predictiveGoals/components/contexts/PredictiveGoalsContext/PredictiveGoalsContext';
import { getBuilderContentForTemplateEvent } from 'modules/predictiveGoals/components/contexts/PredictiveGoalsContext/contextUtils';
import { ReactPortalElementId } from 'core/layouts/types';
import { UpsertPageSkeleton } from 'modules/predictiveGoals/components/upsert/UpsertPageSkeleton';
import { colors } from 'ui/theme';
import noop from 'utils/noop';
import { GhostButton, BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { addNegativeToast } from 'modules/toastContainer';
import { RUMHelpers } from 'services/observability/datadog';
import { useCreateGoal } from './hooks/useCreateGoal';
import PredictiveGoalQueryBuilder from '../../goalBuilder/PredictiveGoalQueryBuilder';
import { PredictiveGoalsHeader } from '../../PredictiveGoalsHeader';
import { LimitExceededState } from '../../LimitExceededState';
import { MutatePreviewUsers } from '../../previewUsers/MutatePreviewUsers';
import { transformForCreation, validateQuery, isValidGoalProperty } from '../../../utils';
import { createActionName, monitorVocab } from '../../../observabilityUtils';
import { isAtGoalLimit, isVocabularyEmpty } from '../../../goalUtils';
import {
  CANCEL_GOAL_UPDATE,
  CREATE_GOAL,
  GOAL_CREATION_LIMIT_ERROR,
  INTERNAL_TEMPLATES_CREATE_GOAL_CONTACT_PROPERTY_MAP,
  INTERNAL_TEMPLATES_CREATE_GOAL_NAME_MAP,
  MAX_GOAL_LIMIT,
  NEW_PREDICTIVE_GOAL,
  PROVIDE_GOAL_NAME,
  PROVIDE_USER_FIELD,
  PROVIDE_VALID_CONTACT_PROPERTY_NAME,
  PROVIDE_VALID_GOAL_NAME,
  TEST_IDS,
} from '../../../constants';
import { EditPredictiveGoalVariables, PredictiveGoalQuery } from '../../../types';
import {
  CreateOrEditActionFooter,
  CreateOrEditActionLayout,
  CreatedAndEditGoalCriteriaWrapper,
  MainContentLayout,
  MainLayoutWrapper,
  SpacedErrorBanner,
  SpacedFirstBanner,
  StyledFullWidth,
} from '../../Styled';
import { PendingAnnouncement } from '../../PendingAnnouncement';

const CreatePredictiveGoalPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [goalName, setGoalName] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [contactProperty, setContactProperty] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
  const [hasCreateLimitError, setHasCreateLimitError] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const {
    createGoal: executeCreateGoal,
    isCreatingGoal,
    isCreatePreviewInvalid,
    isCreatePreviewError,
    usersCount,
    confirmUpdate,
  } = useCreateGoal({
    setHasCreateLimitError,
    setUnsavedChanges: setHasUnsavedChanges,
    onPreviewComplete: () => {
      setIsConfirmModalOpen(true);
    },
    onPreviewError: () => {
      setHasUnsavedChanges(true);
    },
  });
  const {
    goals: { goalData },
    vocab: { vocabData },
    isLoading: isCoreDataLoading,
    areAllReady,
  } = useCoreGoalsContext();
  const { queriedGoal, userTemplateSelection } = usePredictiveGoalsContext();
  const [goalQuery, setGoalQuery] = useState<PredictiveGoalQuery>(queriedGoal);

  useEffect(
    () => setHasUnsavedChanges(goalName !== '' || !isEmpty(goalQuery?.and_clauses)),
    [goalName, goalQuery],
  );

  useEffect(() => {
    if (areAllReady && !vocabData) {
      monitorVocab(vocabData);
    }
  }, [vocabData, areAllReady]);

  useEffect(() => {
    if (userTemplateSelection !== '') {
      setGoalQuery(queriedGoal);
      setGoalName(INTERNAL_TEMPLATES_CREATE_GOAL_NAME_MAP[userTemplateSelection]);
      setContactProperty(
        INTERNAL_TEMPLATES_CREATE_GOAL_CONTACT_PROPERTY_MAP[userTemplateSelection],
      );
    }
  }, [userTemplateSelection, queriedGoal]);
  const templateEventPopoverContent = getBuilderContentForTemplateEvent(
    vocabData,
    userTemplateSelection,
  );

  const validateGoal = () => {
    setSubmitButtonClicked(true);
    const transformedQuery = transformForCreation(goalQuery);
    const errors = validateQuery(transformedQuery);
    if (!goalName) {
      errors.add(PROVIDE_GOAL_NAME);
    } else if (!contactProperty) {
      errors.add(PROVIDE_USER_FIELD);
    }

    if (!isValidGoalProperty(goalName)) {
      errors.add(PROVIDE_VALID_GOAL_NAME);
    }

    if (!isValidGoalProperty(contactProperty)) {
      errors.add(PROVIDE_VALID_CONTACT_PROPERTY_NAME);
    }
    return errors;
  };
  const displayErrors = (errors: Set<string>) => {
    if (errors.size <= 0) {
      return;
    }
    errors.forEach((err) => dispatch(addNegativeToast(err)));
  };

  const saveGoal = () => {
    const listIds: number[] = [];
    const transformedQuery = transformForCreation(goalQuery);
    RUMHelpers.addRUMAction(createActionName('Creating goal'), { goal: transformedQuery });
    setHasUnsavedChanges(false);
    executeCreateGoal({
      name: goalName,
      userProfileField: contactProperty,
      goal: transformedQuery,
      description: goalDescription,
      listIds,
    });
  };

  const onCancel = () => {
    navigate(`/predictive-audiences`);
  };

  const onCreateGoal = () => {
    const errors = validateGoal();
    if (errors.size) {
      displayErrors(errors);
      return;
    }
    saveGoal();
  };

  if (isCoreDataLoading) {
    return (
      <StyledFullWidth>
        <PredictiveGoalsHeader
          background={colors.grey1}
          editableGoal={{} as EditPredictiveGoalVariables}
          goalName={''}
          isLoading
          isReadOnly
          onUpdateGoalName={noop}
        />
        <MainLayoutWrapper data-background-color={colors.white}>
          <MainContentLayout>
            <UpsertPageSkeleton testId="create" />
          </MainContentLayout>
        </MainLayoutWrapper>
        <CreateOrEditActionFooter>
          <CreateOrEditActionLayout>
            <GhostButton onClick={noop} size={BUTTON_SIZES.LARGE} isDisabled>
              {CANCEL_GOAL_UPDATE}
            </GhostButton>
            <PrimaryButton isLoading={false} isDisabled onClick={noop}>
              {CREATE_GOAL}
            </PrimaryButton>
          </CreateOrEditActionLayout>
        </CreateOrEditActionFooter>
      </StyledFullWidth>
    );
  }

  const confirmAndCreateGoal = () => {
    confirmUpdate();
    setIsConfirmModalOpen(false);
  };

  const createGoalConfirmationModal = isConfirmModalOpen && (
    <ConfirmGoalCreationModal
      isModalOpen={isConfirmModalOpen}
      onClose={() => setIsConfirmModalOpen(false)}
      onConfirmClick={confirmAndCreateGoal}
    />
  );

  const portalPath = document.getElementById(ReactPortalElementId.announcements);
  const isVocabEmpty = isVocabularyEmpty(vocabData);
  const canCreateGoals = !isAtGoalLimit(MAX_GOAL_LIMIT, goalData);
  const isCreateDisabled = isCreatingGoal || isVocabEmpty || hasCreateLimitError;
  const maybeCreateError = hasCreateLimitError && (
    <MainLayoutWrapper>
      <MainContentLayout>
        <SpacedFirstBanner>
          <SpacedErrorBanner>{GOAL_CREATION_LIMIT_ERROR}</SpacedErrorBanner>
        </SpacedFirstBanner>
      </MainContentLayout>
    </MainLayoutWrapper>
  );

  let createPageContent = (
    <>
      {maybeCreateError}
      <CreatedAndEditGoalCriteriaWrapper>
        <PredictiveGoalQueryBuilder
          contactProperty={contactProperty}
          canEditName
          goalDescription={goalDescription}
          goalName={goalName}
          goalQuery={goalQuery}
          hasUnsavedChanges={hasUnsavedChanges}
          setContactProperty={setContactProperty}
          setGoalDescription={setGoalDescription}
          setGoalName={setGoalName}
          setGoalQuery={setGoalQuery}
          submitButtonClicked={submitButtonClicked}
          templateEventPopoverContent={templateEventPopoverContent}
          transformQuery={transformForCreation}
          vocabulary={vocabData}
        />
      </CreatedAndEditGoalCriteriaWrapper>
      <CreateOrEditActionFooter>
        <CreateOrEditActionLayout>
          <GhostButton onClick={onCancel} size={BUTTON_SIZES.LARGE}>
            {CANCEL_GOAL_UPDATE}
          </GhostButton>
          <MutatePreviewUsers
            primaryButtonConfig={{ text: CREATE_GOAL, testId: TEST_IDS.CreateGoalBtn }}
            isDisabled={isCreateDisabled}
            isLoading={isCreatingGoal}
            onClick={onCreateGoal}
            showPreviewContent={isCreatePreviewInvalid || isCreatePreviewError}
            isError={isCreatePreviewError}
            usersCount={usersCount}
          />
        </CreateOrEditActionLayout>
      </CreateOrEditActionFooter>
    </>
  );

  if (!canCreateGoals) {
    createPageContent = <LimitExceededState />;
  }

  return (
    <StyledFullWidth>
      {isVocabEmpty && portalPath && createPortal(<PendingAnnouncement />, portalPath)}
      <PredictiveGoalsHeader
        background={colors.grey1}
        isReadOnly
        goalName={NEW_PREDICTIVE_GOAL}
        onUpdateGoalName={noop}
        editableGoal={{} as EditPredictiveGoalVariables}
      />
      {createPageContent}
      {createGoalConfirmationModal}
    </StyledFullWidth>
  );
};

export default CreatePredictiveGoalPage;
